/* components/PatientPlan/PatientPlan.tsx */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable no-nested-ternary */

import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectOrganization } from '@brands/store/selectors/organization';
import React, { useCallback, useMemo, useState } from 'react';
import { HiOutlineUserCircle } from 'react-icons/hi';
import { MdCheckCircle, MdOutlineCheckCircle } from 'react-icons/md';
import { toast } from 'react-toastify';

import Container from '../../Components/Container/Container';
import { usePatientPlan } from '../../hooks/usePatientPlans';
import { removePlanMember } from '../../services/patient_plan/services';
import { GroupMember, GroupSummary, PlanType } from '../../services/patient_plan/types/types';
import {
  formatDate,
  getPlanDescription,
  getPlanName,
  iconByType,
  initialsFrom,
  planTypeLabel,
  roleText,
  safeCount,
} from './assets/helpers';
import AddDependentModal from './components/AddDependentModal/AddDependentModal';
import AddMembersModal from './components/AddMembersModal/AddMembersModal';
import ConfirmRemoveMemberModal from './components/ConfirmRemoveMemberModal/ConfirmRemoveMemberModal';
import MemberDetailsModal from './components/MemberDetailsModal/MemberDetailsModal';
import styles from './styles.module.scss';

type MemberForModal = GroupMember & {
  email?: string | null;
  avatar_url?: string | null;
  dependent?: boolean;
  free_total?: number | null;
  free_remaining?: number | null;
  unlimited?: boolean;
};

const EmptyState: React.FC = () => {
  return (
    <div
      style={{
        border: '1px solid rgba(15, 23, 42, 0.08)',
        borderRadius: 24,
        padding: '72px',
        minHeight: 220,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        width: '100%',
      }}
      aria-live="polite"
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>You don’t have an active plan yet.</div>
      </div>
    </div>
  );
};

const PatientPlan: React.FC = () => {
  const screen = useWindowSize();
  const roleNavigate = useRoleNavigate();
  const { userInfo } = useAppSelector(selectAuth);
  const { currentOrganization } = useAppSelector(selectOrganization);
  const { loading, error, group, refresh } = usePatientPlan();

  const [isAddMembersOpen, setIsAddMembersOpen] = useState(false);
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MemberForModal | null>(null);
  const [addDependentModal, setAddDependentModal] = useState(false);
  const [confirmMember, setConfirmMember] = useState<MemberForModal | null>(null);

  const isMobile = (screen?.width ?? 0) < 480;
  const isDesktopWide = (screen?.width ?? 0) >= 1289;

  const containerStyle = useMemo<React.CSSProperties>(
    () => ({
      justifyContent: isDesktopWide ? 'center' : 'start',
      overflowY: isDesktopWide ? 'hidden' : 'auto',
    }),
    [isDesktopWide],
  );

  const planTypeText = useMemo(() => (group ? planTypeLabel(group.type) : ''), [group]);
  const headerTitleLeft = useMemo(() => (group ? (getPlanName(group as GroupSummary) ?? '') : ''), [group]);
  const headerTitleRight = useMemo(() => (headerTitleLeft ? `(${planTypeText})` : ''), [headerTitleLeft, planTypeText]);

  const orgId = currentOrganization?.id;
  const orgName = currentOrganization?.name;

  const memberCount = useMemo(() => safeCount(group?.member_count), [group?.member_count]);
  const maxMembers = useMemo(() => safeCount(group?.max_members), [group?.max_members]);
  const accountText = useMemo(
    () => (group ? `${memberCount} of ${maxMembers} accounts being used` : ''),
    [group, memberCount, maxMembers],
  );
  const remainingSeats = Math.max(0, (group?.max_members ?? 0) - (group?.member_count ?? 0));
  const currentUserRole = useMemo(
    () => group?.members?.find((m: GroupMember) => m.id === userInfo?.id)?.role ?? null,
    [group?.members, userInfo?.id],
  );

  const seatUsedKeys = useMemo(() => Array.from({ length: memberCount }, (_, i) => `seat-used-${i}`), [memberCount]);

  const seatFreeKeys = useMemo(() => {
    const free = Math.max(0, maxMembers - memberCount);
    return Array.from({ length: free }, (_, i) => `seat-free-${i}`);
  }, [maxMembers, memberCount]);

  const selfMember = useMemo(
    () => group?.members?.find((m: GroupMember) => m.id === userInfo?.id) as MemberForModal | undefined,
    [group?.members, userInfo?.id],
  );

  const selfUnlimited = useMemo(
    () => Boolean(group?.unlimited) || Boolean(selfMember?.unlimited),
    [group?.unlimited, selfMember?.unlimited],
  );

  const selfTotalFreeRaw: number | typeof Infinity = selfUnlimited
    ? Infinity
    : (selfMember?.free_total ?? group?.free_cases_per_year ?? 0);

  const selfTotalFree = useMemo(
    () => (selfTotalFreeRaw === Infinity ? Infinity : safeCount(selfTotalFreeRaw)),
    [selfTotalFreeRaw],
  );

  const selfRemainingRaw: number | typeof Infinity = selfUnlimited
    ? Infinity
    : (selfMember?.free_remaining ??
      // controller also returns top-level free_remaining for caller; use as fallback
      group?.free_remaining ??
      (typeof selfTotalFree === 'number' ? selfTotalFree : 0));

  const selfRemaining = useMemo(
    () => (selfRemainingRaw === Infinity ? Infinity : Math.max(0, safeCount(selfRemainingRaw))),
    [selfRemainingRaw],
  );

  const selfUsed = useMemo(
    () => (selfTotalFree === Infinity ? 0 : Math.max(0, (selfTotalFree as number) - (selfRemaining as number))),
    [selfTotalFree, selfRemaining],
  );

  const serviceKeys = useMemo(() => {
    if (selfTotalFree === Infinity) return [];
    const count = safeCount(selfTotalFree);
    return Array.from({ length: count }, (_, i) => `svc-${i}`);
  }, [selfTotalFree]);

  const memberUsage = useCallback(
    (m: MemberForModal) => {
      const unlimited = Boolean(group?.unlimited) || Boolean(m.unlimited);
      if (unlimited) return { used: 0, total: Infinity };
      const total = safeCount(m.free_total ?? group?.free_cases_per_year ?? 0);
      if (total <= 0) return null;

      const isSelf = m.id === userInfo?.id;
      const remainingRaw = m.free_remaining ?? (isSelf ? (selfRemaining === Infinity ? total : selfRemaining) : total);
      const remaining = Math.max(0, safeCount(remainingRaw));
      const used = Math.max(0, total - remaining);
      return { used, total };
    },
    [group?.free_cases_per_year, group?.unlimited, selfRemaining, userInfo?.id],
  );

  const openAddMembers = useCallback(() => setIsAddMembersOpen(true), []);
  const closeAddMembers = useCallback(() => setIsAddMembersOpen(false), []);
  const handleGroupUpdated = useCallback(() => {
    closeAddMembers();
    refresh();
  }, [closeAddMembers, refresh]);

  const handleAddNewDependent = useCallback(() => {
    setAddDependentModal(true);
    closeAddMembers();
  }, [closeAddMembers, setAddDependentModal]);

  const openMemberDetails = useCallback((m: GroupMember) => {
    const decorated: MemberForModal = {
      ...m,
      email: (m as GroupMember).email ?? null,
      dependent: m.role !== 'OWNER',
      free_total: (m as GroupMember).free_total ?? null,
      free_remaining: (m as GroupMember).free_remaining ?? null,
      unlimited: Boolean((m as GroupMember).unlimited),
    };
    setSelectedMember(decorated);
    setIsMemberModalOpen(true);
  }, []);

  const closeMemberDetails = useCallback(() => {
    setIsMemberModalOpen(false);
    setSelectedMember(null);
  }, []);

  const handleRemoveMember = useCallback(
    async (groupId: number, memberId: number): Promise<void> => {
      try {
        await removePlanMember(groupId, memberId);
        toast.success('Member removed successfully');
      } catch {
        toast.error('Failed to remove member');
      } finally {
        setIsMemberModalOpen(false);
        refresh();
      }
    },
    [refresh],
  );

  const onViewHistory = useCallback(
    (memberId: number) => {
      const currentUserId = userInfo?.id;
      if (!currentUserId) return;

      // Always allow user to view their own history
      if (memberId === currentUserId) {
        roleNavigate('/my-care#care_history');
        return;
      }

      const member = group?.members?.find((m: GroupMember) => m.id === memberId);
      const currentMember = group?.members?.find((m: GroupMember) => m.id === currentUserId);

      const isDependent = member?.role === 'DEPENDENT';
      const isOwner = currentMember?.role === 'OWNER';

      if (isDependent && isOwner) {
        roleNavigate('/my-care#care_history', {
          state: { dependentId: memberId },
        });
      }
    },
    [group?.members, roleNavigate, userInfo?.id],
  );

  const BannerTitle = useCallback(() => {
    const orgDisplay =
      group &&
      (orgId && orgId === (group as any).organization_id ? (orgName ?? orgId) : (group as any).organization_id);

    return (
      <div className={styles.bannerTitle}>
        <div className={styles.bannerIcon}>
          {group ? <img src={iconByType[group.type as PlanType]} alt="" /> : <div className={styles.iconSkeleton} />}
        </div>

        {isMobile ? (
          <div className={styles.bannerTitleContainer}>
            <div className={styles.bannerTitleStrong}>
              {headerTitleLeft || (loading ? 'Loading…' : '—')}
              {headerTitleRight && <span className={styles.bannerType}> {headerTitleRight}</span>}
            </div>
            <div>{group && <span className={styles.bannerPayer}> {orgDisplay}</span>}</div>
          </div>
        ) : (
          <>
            <span className={styles.bannerTitleStrong}>
              {headerTitleLeft || (loading ? 'Loading…' : '—')}
              {headerTitleRight && <span className={styles.bannerType}> {headerTitleRight}</span>}
            </span>
            {group && <span className={styles.bannerPayer}> {orgDisplay}</span>}
          </>
        )}
      </div>
    );
  }, [group, orgId, orgName, isMobile, headerTitleLeft, headerTitleRight, loading]);

  const showEmpty = !loading && !group;

  return (
    <Container
      patientDashboard
      childClassName={styles.dashboard}
      containerStyle={containerStyle}
      childrenStyle={{ alignItems: 'start' }}
    >
      <div className={styles.planContainer}>
        {/* Page heading — h1 in the design, like every other screen's. */}
        <h1 className="fs-unmask">My plan</h1>

        {showEmpty ? (
          <EmptyState />
        ) : (
          <div className={styles.card}>
            <div className={styles.banner}>
              <div className={styles.bannerText}>
                <BannerTitle />

                <div className={styles.bannerSub}>
                  {loading ? (
                    <>Loading your plan…</>
                  ) : group ? (
                    <>{getPlanDescription(group)}</>
                  ) : (
                    <>You don’t have an active plan yet.</>
                  )}
                </div>

                <div className={styles.bannerMeta}>
                  {group?.joined_at ? <>Joined {formatDate(group?.joined_at)}</> : null}
                </div>
              </div>
              <div className={styles.bannerExpiry} aria-live="polite">
                {/* Previously always "12/31" of the current year, which no
                    stored value backed. Plans may legitimately have no expiry,
                    and saying so is better than naming a date we invented. */}
                {!group && <>Expires —</>}
                {group && group.expiry_date && <>Expires on {formatDate(group.expiry_date)}</>}
                {group && !group.expiry_date && <>Does not expire</>}
              </div>
            </div>

            {error && (
              <div className={styles.errorBar} role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <div className={styles.body}>
              {group?.type !== 'INDIVIDUAL' && (
                <section className={styles.colLeft} aria-labelledby="members-title">
                  <h3 id="members-title">Plan members</h3>

                  <div className={styles.seatRow} aria-label="Plan seats">
                    {seatUsedKeys.map((k) => (
                      <MdCheckCircle key={k} className={styles.seatCheck} />
                    ))}
                    {seatFreeKeys.map((k) => (
                      <HiOutlineUserCircle key={k} className={styles.seatUser} />
                    ))}
                  </div>

                  <div className={styles.seatText}>
                    {group ? accountText : loading ? 'Loading accounts…' : 'No accounts'}
                  </div>

                  <ul className={styles.memberList}>
                    {(group?.members ?? []).map((m: GroupMember) => {
                      const label =
                        [m.first_name, m.last_name].filter(Boolean).join(' ') || (m as any).email || `User #${m.id}`;
                      const initials = initialsFrom(label);
                      const isRemoved = !m.active;

                      return (
                        <li key={m.id} className={styles.memberItem}>
                          <div className={styles.avatar} aria-hidden>
                            {initials}
                          </div>
                          <div className={styles.memberMeta}>
                            <div className={styles.memberName}>{label}</div>
                            {isRemoved && <div className={styles.memberRemoved}>Removed from plan</div>}
                            {!isRemoved && <div className={styles.memberRole}>{roleText(m)}</div>}
                          </div>
                          {!isRemoved && currentUserRole === 'OWNER' && (
                            <button
                              type="button"
                              className={styles.chevronBtn}
                              aria-label={`View ${label}`}
                              onClick={() => openMemberDetails(m)}
                              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openMemberDetails(m)}
                            >
                              ›
                            </button>
                          )}
                        </li>
                      );
                    })}
                    {(!group || (group.members ?? []).length === 0) && !loading && (
                      <li className={styles.memberItem}>No members yet</li>
                    )}
                  </ul>

                  {currentUserRole === 'OWNER' && (
                    <button
                      type="button"
                      className={styles.addBtn}
                      onClick={openAddMembers}
                      disabled={!group || loading || remainingSeats <= 0}
                    >
                      <span className={styles.addIcon}>+</span> Add Members
                    </button>
                  )}
                </section>
              )}
              {group?.type !== 'INDIVIDUAL' && <hr />}

              <section className={styles.colRight} aria-labelledby="remaining-title">
                {((group?.free_cases_per_year && group?.free_cases_per_year > 0) || group?.unlimited) && (
                  <>
                    <h3 id="remaining-title">Your remaining services</h3>

                    {loading ? (
                      <p className={styles.remainingText}>Loading…</p>
                    ) : !group ? (
                      <p className={styles.remainingText}>You don’t have an active plan.</p>
                    ) : selfUnlimited || selfTotalFree === Infinity ? (
                      <>
                        <div className={styles.badgesRow} style={{ fontWeight: 600 }} aria-hidden>
                          Unlimited
                        </div>
                        <p className={styles.remainingText}>Unlimited free services with this plan</p>
                      </>
                    ) : typeof selfTotalFree === 'number' && selfTotalFree > 0 ? (
                      <>
                        <div
                          className={styles.badgesRow}
                          role="progressbar"
                          aria-valuemin={0}
                          aria-valuenow={selfUsed}
                          aria-valuemax={selfTotalFree}
                          aria-label="Free services used"
                        >
                          {serviceKeys.map((k, idx) =>
                            idx < selfUsed ? (
                              <MdCheckCircle key={k} className={styles.badgeUsed} aria-hidden />
                            ) : (
                              <MdOutlineCheckCircle key={k} className={styles.badgeFree} aria-hidden />
                            ),
                          )}
                        </div>
                        <p className={styles.remainingText}>
                          You have used {selfUsed} of {selfTotalFree} free services that come with this plan
                        </p>
                      </>
                    ) : (
                      <p className={styles.remainingText}>No free services with this plan</p>
                    )}
                  </>
                )}
                <button
                  type="button"
                  className={styles.linkBtn}
                  disabled={!group || loading}
                  onClick={() => onViewHistory(userInfo?.id ?? 0)}
                >
                  Care History
                </button>
              </section>
            </div>
          </div>
        )}
      </div>

      {group && (
        <AddMembersModal
          isOpen={isAddMembersOpen}
          onClose={closeAddMembers}
          group={group}
          onGroupUpdated={handleGroupUpdated}
          onAddNewDependent={handleAddNewDependent}
        />
      )}

      {group && (
        <MemberDetailsModal
          isOpen={isMemberModalOpen}
          onClose={closeMemberDetails}
          group={group}
          member={selectedMember}
          usage={selectedMember ? (memberUsage(selectedMember) ?? undefined) : undefined}
          onRequestRemove={(m) => {
            setConfirmMember(m as MemberForModal);
            setIsMemberModalOpen(false);
          }}
          onViewHistory={(memberId) => onViewHistory(memberId)}
        />
      )}
      <ConfirmRemoveMemberModal
        isOpen={Boolean(confirmMember)}
        memberName={
          confirmMember
            ? [confirmMember.first_name, confirmMember.last_name].filter(Boolean).join(' ') ||
              (confirmMember as any).email ||
              `User #${confirmMember.id}`
            : undefined
        }
        onCancel={() => setConfirmMember(null)}
        onConfirm={() => {
          if (group && confirmMember?.id) {
            setConfirmMember(null); // close confirm first
            handleRemoveMember(group.id, confirmMember.id); // will close details + refresh
          }
        }}
      />
      {addDependentModal && (
        <AddDependentModal
          isOpen={addDependentModal}
          setIsOpen={setAddDependentModal}
          patientPlanId={group ? group.id : undefined}
          onGroupUpdated={handleGroupUpdated}
        />
      )}
    </Container>
  );
};

export default PatientPlan;
